import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, RefreshCw } from 'lucide-react';
import { useAuth } from 'contexts/AuthContext';
import {
  INSTALACIONES, INSTALACION_DEFAULT, getChecklist, instalacionesConChecklist,
} from 'data/checklist';
import { fetchInspeccionOriginal } from 'services/inspeccionService';
import { fechaLarga, hoyCR } from 'lib/fechaCR';
import { ESTADO_PILL } from 'lib/estadoUi';
import Asistente, { INACTIVIDAD_KIOSCO_MS } from './Asistente';
import EncabezadoAsistente from './EncabezadoAsistente';
import EncabezadoApp from 'components/EncabezadoApp';
import { ElegirInstalacion, InstalacionPendiente } from './ElegirInstalacion';
import { ConfirmarSalida, useSalidaPin } from './SalidaPin';
import { mensajeError } from 'lib/errores';

export { INACTIVIDAD_KIOSCO_MS };

// /inspeccion: decide qué asistente abrir.
//   ?corrige=<id>          corrección de esa inspección (solo admin con SSO)
//   ?instalacion=<id>      esa instalación (si su checklist está activo)
//   sin parámetros         si hay más de una instalación con checklist activo,
//                          primero "¿Qué va a inspeccionar?"; si hay una, esa.
// `instalaciones` se puede inyectar (pruebas); por defecto, data/checklist.

// Marco de las pantallas previas al asistente: mismo encabezado y ancho, y el
// "Salir" del PIN (que también avisa de inspecciones sin enviar).
function Marco({ subtitulo, children, contarPendientes }) {
  const { esPin, nombrePin, signOut } = useAuth();
  const salir = () => {
    salida.cancelar();
    signOut?.();
  };
  const salida = useSalidaPin({ salir, contarPendientes });
  return (
    <>
      <EncabezadoApp onSalir={() => salida.pedir({ hayAvance: false })} />
      <div className="mx-auto w-full max-w-xl px-4">
      <EncabezadoAsistente
        subtitulo={[subtitulo, esPin ? nombrePin : null].filter(Boolean).join(' · ')}
      />
      <main className="pb-16 pt-4">
        {salida.confirmar && (
          <ConfirmarSalida
            hayAvance={false}
            pendientes={salida.confirmar.pendientes}
            onCancelar={salida.cancelar}
            onSalir={salir}
          />
        )}
        {children}
      </main>
      </div>
    </>
  );
}

function Aviso({ titulo, texto, children }) {
  return (
    <div role="alert" className={`rounded-2xl border-2 p-4 ${ESTADO_PILL.alerta}`}>
      <h2 tabIndex={-1} className="flex items-start gap-2 text-xl font-bold leading-tight text-foreground focus:outline-none">
        <AlertCircle size={22} className="mt-0.5 shrink-0" aria-hidden="true" />
        {titulo}
      </h2>
      {texto ? <p className="mt-2 text-base text-foreground">{texto}</p> : null}
      <div className="mt-4 flex flex-col gap-3 sm:flex-row">{children}</div>
    </div>
  );
}

const BOTON_SECUNDARIO =
  'flex min-h-[56px] flex-1 items-center justify-center gap-2 rounded-2xl border-2 border-foreground/30 bg-white px-4 text-lg font-bold text-foreground';

// Carga la inspección a corregir y valida que se pueda corregir.
function Correccion({ id, mapa, asistenteProps }) {
  const navigate = useNavigate();
  const { esPin, esKiosco, isAdmin } = useAuth();
  const autorizado = !!isAdmin && !esPin && !esKiosco;
  const [estado, setEstado] = useState({ cargando: autorizado });

  const cargar = useCallback(() => {
    let vivo = true;
    setEstado({ cargando: true });
    Promise.resolve()
      .then(() => fetchInspeccionOriginal(id))
      .then((original) => { if (vivo) setEstado({ original }); })
      .catch((e) => { if (vivo) setEstado({ error: mensajeError(e, 'No se pudo cargar la inspección.') }); });
    return () => { vivo = false; };
  }, [id]);

  useEffect(() => (autorizado ? cargar() : undefined), [autorizado, cargar]);

  const volverDetalle = (
    <button type="button" onClick={() => navigate(`/inspecciones/${id}`)} className={BOTON_SECUNDARIO}>
      <ArrowLeft size={20} aria-hidden="true" />
      Volver a la inspección
    </button>
  );
  const volverInicio = (
    <button type="button" onClick={() => navigate('/')} className={BOTON_SECUNDARIO}>
      <ArrowLeft size={20} aria-hidden="true" />
      Volver
    </button>
  );

  let cuerpo;
  if (!autorizado) {
    cuerpo = (
      <Aviso
        titulo="Solo administración puede registrar correcciones"
        texto="La corrección de una inspección se hace desde el detalle, con una cuenta de administración (no con PIN ni desde el kiosco)."
      >
        {volverInicio}
      </Aviso>
    );
  } else if (estado.cargando) {
    cuerpo = <p role="status" className="text-base text-foreground">Cargando la inspección a corregir…</p>;
  } else if (estado.error) {
    cuerpo = (
      <Aviso titulo="No se pudo cargar la inspección a corregir" texto={estado.error}>
        <button type="button" onClick={cargar} className={BOTON_SECUNDARIO}>
          <RefreshCw size={20} aria-hidden="true" />
          Reintentar
        </button>
        {volverDetalle}
      </Aviso>
    );
  } else if (!estado.original) {
    cuerpo = (
      <Aviso titulo="No se encontró la inspección a corregir" texto="Puede que el enlace esté incompleto o que la inspección no exista.">
        {volverInicio}
      </Aviso>
    );
  } else if (estado.original.anulada) {
    cuerpo = (
      <Aviso
        titulo="Esa inspección está anulada"
        texto="Una inspección anulada no se corrige. Si hace falta, registre la corrección sobre la que sigue vigente."
      >
        {volverDetalle}
      </Aviso>
    );
  } else {
    const checklist = getChecklist(estado.original.instalacion || INSTALACION_DEFAULT, mapa);
    if (!checklist || checklist.items.length === 0) {
      cuerpo = (
        <Aviso titulo="No se puede corregir esta inspección" texto="Su instalación no tiene un checklist activo en esta versión de la app.">
          {volverDetalle}
        </Aviso>
      );
    } else {
      // La corrección hereda la instalación de la original.
      return <Asistente {...asistenteProps} checklist={checklist} correccion={{ original: estado.original }} />;
    }
  }
  return <Marco subtitulo="Corrección de inspección">{cuerpo}</Marco>;
}

export default function Inspeccion({ instalaciones = INSTALACIONES, contarPendientes, ...asistenteProps }) {
  const [params] = useSearchParams();
  const corrige = params.get('corrige');
  const pedida = params.get('instalacion');
  const disponibles = instalacionesConChecklist(instalaciones);
  const todas = Object.values(instalaciones);
  const variasDisponibles = disponibles.length > 1;

  // Instalación de entrada: la del enlace si existe; si no, la única activa.
  const inicial = getChecklist(pedida, instalaciones)
    ? pedida
    : (variasDisponibles ? null : (disponibles[0]?.id || INSTALACION_DEFAULT));
  const [elegida, setElegida] = useState(inicial);

  const props = { ...asistenteProps, contarPendientes };
  if (corrige) return <Correccion id={corrige} mapa={instalaciones} asistenteProps={props} />;

  const checklist = getChecklist(elegida, instalaciones);
  if (!checklist) {
    return (
      <Marco subtitulo={fechaLarga(hoyCR())} contarPendientes={contarPendientes}>
        <ElegirInstalacion instalaciones={todas} onElegir={setElegida} />
      </Marco>
    );
  }
  if (checklist.pendiente || checklist.items.length === 0) {
    return (
      <Marco subtitulo={fechaLarga(hoyCR())} contarPendientes={contarPendientes}>
        <InstalacionPendiente checklist={checklist} alternativas={disponibles} onElegir={setElegida} />
      </Marco>
    );
  }
  return (
    <Asistente
      key={checklist.id}
      {...props}
      checklist={checklist}
      mostrarInstalacion={todas.length > 1}
      onVolverAElegir={variasDisponibles ? () => setElegida(null) : undefined}
    />
  );
}

